import type { VectorKind } from '../../api/models'
import type { BasemapTone } from '../basemaps'
import type { Layer } from '../layer'
import { GeoJsonLayer } from './geoJsonLayer'
import { ModelLayer, modelRun, modelWindow } from './modelLayer'
import { ModelVectorLayer } from './modelVectorLayer'
import { RadarMapLayer } from './radarMapLayer'
import { NHC_ATTRIB, outlookUrl, parseOutlook, parseStorms, stormUrls } from './nhc'
import { MD_SERVICE, OUTLOOKS, parseMds, parseOutlooks, queryUrl, SPC_ATTRIB } from './spc'
import type { FrameMode } from './timeSource'
import { WmsStaticLayer } from './wmsStaticLayer'
import { WmsTimeLayer } from './wmsTimeLayer'
import { XyzFramesLayer } from './xyzFramesLayer'
import type { WmsTarget } from './wms'

/**
 * The overlay catalogue. Mirrors `basemaps.ts`: a flat table, with nothing
 * else in the app knowing a provider by name.
 *
 * These layers are rendered by the upstream, so the units and colormaps are
 * the upstream's and we only label them. The *backend* registry CLAUDE.md
 * describes is for Phase 3's model fields, where we own the rendering.
 *
 * All URLs probed 2026-09-16 against nowCOAST's GeoServer; see
 * docs/phase2-overlays-plan.md section 3.
 */

export type LayerGroup =
  'radar' | 'observations' | 'satellite' | 'models' | 'outlooks' | 'tropical' | 'hazards'

/**
 * Which clock a layer's frames are on.
 *
 * `live` is wall-clock time anchored on now: every nowCOAST row publishes a
 * TIME dimension around the present, and `nearestValue` snapping is what lets
 * a four-minute radar loop and a fifteen-minute lightning field share one
 * slider honestly.
 * `run` is a model run's own step table — a lead window over up to sixteen
 * days of forecast steps, strided to the frame budget.
 *
 * The two cannot share a slider. Twelve radar frames spanning 48 minutes
 * beside twelve HRRR steps spanning 48 hours is 24 evenly spaced stops where
 * half are four minutes apart and half are four hours apart; scrubbing either
 * half leaves every layer on the other clock pinned at one end, not animating.
 * So `toggleLayer` keeps one timeline active at a time — turning on a model
 * field turns radar off, and turning radar on turns the model field off.
 *
 * `null` is a layer with no frames at all: alerts and the SPC products belong
 * to neither clock and this rule never touches them, which is the same reason
 * they are not `exclusive`.
 */
export type Timeline = 'live' | 'run'

/**
 * Where a layer's colour bar comes from.
 *
 * A union, mirroring what `Readout` already did in Phase 2.5. `wms` reads
 * GeoServer's own `GetLegendGraphic` document so the bar cannot drift from
 * imagery somebody else rendered; `model` reads `/api/models`, which is the
 * table that coloured our own pixels. Same principle, two upstreams.
 */
export type LegendRef =
  | ({ kind: 'wms'; units: string } & WmsTarget)
  | { kind: 'model'; model: string; variable: string }
  /**
   * A contour or barb layer, which has no ramp to draw.
   *
   * An isobar field's legend is its *interval* — "every 4 hPa, every 20 hPa
   * heavier" — and a barb's is the convention that decodes it. Both are one
   * sentence rather than a gradient, and both come out of the catalog's own
   * `contour`/`barb` block so nothing here restates what MSLP is.
   */
  | { kind: 'vector'; model: string; variable: string; vector: VectorKind }
  /** The radar map's two ramps, rain and snow, out of `/api/now/map`. */
  | { kind: 'nowcast' }

/**
 * How a layer's value at a point is reported in the map menu.
 *
 * `null` means the layer reports no meaningful value at a point — GOES
 * imagery is stretched to 0–255 for rendering, so "58 brightness temp" would
 * be a fabrication, and the alerts layer answers with the alerts themselves.
 * Those layers show no number.
 *
 * The `kind` exists because `probe.ts` used to dispatch on `legend` being
 * absent, which stood in for "is this a nowCOAST raster". That proxy held for
 * the three Phase 2 layer kinds by accident and breaks on the fourth: an SPC
 * outlook has no legend and is very much queryable. The registry says what it
 * means instead.
 */
export type Readout =
  /** A raster `GetFeatureInfo`, whose `Band1` is a measurement in the layer's units. */
  | {
      kind: 'raster'
      decimals: number
      /**
       * The plotted range, for layers whose nodata sentinel is an in-band
       * number. Base reflectivity's colormap runs 1–80 and labels 81 and 255
       * `nodata`, so anything above 80 is "no coverage", not an extreme storm.
       *
       * The NDFD rows did the same with 9999 for any point outside the
       * forecast domain, which was "9999 °F" in the map menu over the Gulf
       * until they had a range. (Those rows are gone: nowCOAST removed every
       * NDFD workspace, and they 404 as of 2026-09-29.)
       */
      range?: [number, number]
      /** What to query. Same target as the legend for every current row, but
       *  said explicitly rather than inferred from one. */
      target: WmsTarget
    }
  /** A vector layer already in the browser: ask MapLibre, not the network. */
  | { kind: 'feature' }
  /**
   * Our own model field: `/api/point`, asked with the forecast hour the layer
   * is *drawing* rather than the slider's index. Units, decimals, the floor
   * and the sentinel are the API's; restating them here is the second list
   * `GROUP_LABELS` already taught us about.
   */
  | { kind: 'model'; model: string; variable: string }
  /**
   * The radar map: the index under the point in the plate on screen, put into
   * the "Next hour" section's words. There is no `/api/point` for it — a past
   * frame and a forecast frame exist only as plates — and the plate *is* the
   * forecast, at the cell, not an image of one.
   */
  | { kind: 'nowcast' }

export interface LayerDef {
  /** Stable; persisted in localStorage. */
  id: string
  label: string
  group: LayerGroup
  /**
   * Set only on a row from one of our own models, and it is what makes the
   * Models group render as a two-level picker rather than 37 checkboxes.
   * `ModelPicker` derives its chips from these; nothing keeps a second list.
   */
  model?: string
  variable?: string
  /**
   * Which artifact of that field this row draws: absent for the plate,
   * `contour` or `barb` for a vector row. `ModelPicker` groups on it, so a
   * model's fields and the lines drawn through them are one list rather than
   * two.
   */
  vector?: VectorKind
  /**
   * The basemap tone this layer needs under it when the basemap is Match
   * theme. Set on our model plates: a continuous field over the Dark basemap
   * turns the ground muddy, and its dark roads and dark-haloed labels sit on
   * top as black scribble (Phase 10 plan §7.1). Contours, barbs and radar
   * leave it unset and read fine on either. A basemap somebody picked is never
   * overridden.
   */
  ground?: BasemapTone
  attribution: string
  defaultOpacity: number
  /** Only one member of a group may be active at a time (two radars is noise). */
  exclusive: boolean
  /** Which clock this layer's frames are on; null for a layer with no frames. */
  timeline: Timeline | null
  /** The upstream renders these; the legend states them. */
  units: string
  legend: LegendRef | null
  /** null when the layer reports no meaningful value at a point. */
  readout: Readout | null
  create(): Layer
}

const NOWCOAST = 'https://nowcoast.noaa.gov/geoserver'
const NOAA_ATTRIB = '<a href="https://nowcoast.noaa.gov/">NOAA nowCOAST</a>'
const IEM_ATTRIB = '<a href="https://mesonet.agron.iastate.edu/">Iowa State Mesonet</a>'
const MRMS_ATTRIB =
  '<a href="https://registry.opendata.aws/noaa-mrms-pds/">NOAA MRMS radar via NODD</a>'
/** Plain text: `Legend.svelte` prints a custom layer's credit as text. */
const MRMS_CREDIT = 'NOAA MRMS radar via NODD; the next hour extrapolated here'

/** The alerts layer is the one entry other modules address by name: it is tappable. */
export const ALERTS_ID = 'alerts'
/** Likewise: the map menu lists the discussions under the finger by name. */
export const MD_ID = 'spc-md'
export const ALERTS_LAYER_NAME = 'alerts:watches_warnings_advisories'
export const ALERTS_TARGET: WmsTarget = {
  base: `${NOWCOAST}/alerts/wms`,
  layerName: ALERTS_LAYER_NAME,
}

/**
 * Frame budget for the **nowCOAST** rows. Radar's native cadence is ~4 min, so
 * 12 frames is ~48 minutes of loop — the right amount of context for "is it
 * coming toward me". Every GetMap is a live render at NOAA's end, so more
 * frames is not free for them and not free for a phone's memory.
 */
export const FRAMES_DESKTOP = 12
export const FRAMES_PHONE = 6

/**
 * Frame budget for **our own model plates**, which is a different number for
 * different reasons.
 *
 * nowCOAST's 12 was never derived from the model case; the model rows simply
 * inherited it, and the result was a 15-minute product drawn at 1h45m spacing.
 * With 73 steps in a sub-hourly HRRR run, twelve evenly spaced frames land on
 * leads 0, 105, 195, 300 … — coarser than the plain hourly HRRR beside it,
 * which is the opposite of why that product exists.
 *
 * None of nowCOAST's three reasons transfer:
 *
 * * **Nobody renders these on demand.** They are our own PNGs, pre-rendered
 *   once per run, served `immutable`, and matched by a `CacheFirst`
 *   service-worker rule. A frame fetched during one loop is free for the rest
 *   of the run's life.
 * * **Memory is flat in the frame count.** `ImageFramesLayer` keeps one
 *   texture and swaps it with `updateImage`, so 73 frames costs the same
 *   9.3 MB of resident RGBA as 12. The 112 MB in that file's header is the
 *   cost of the *mounted-frames* approach it exists to avoid.
 * * **The fetch is incremental.** `prefetch()` warms -1/+3 around the cursor,
 *   so the bytes arrive as the loop plays rather than at `load()`.
 *
 * What is left is bandwidth while playing and the slider's legibility, and
 * both are bounded by the *window* rather than by this number — which is the
 * honest division of labour between the two controls. Measured against a real
 * `data/models` tree on 2026-09-19, a whole +48 h run is 10.9 MB (HRRR refc
 * @2048), 14.9 MB (HRRR 15m refc @2048), 39 MB (HRRR wind10 @2048, the worst
 * field we ship) and 3 MB (GFS t2m @512).
 *
 * 96 on desktop is chosen so that no field in a default deployment strides at
 * all: `WEATHER_MODEL_MAX_LEAD_MINUTES` defaults to 2880, which caps every
 * model at 49 hourly steps and sub-hourly HRRR at 73. It bites only when a
 * deployment raises that lever — GFS's native axis is 208 steps — and there
 * striding is the right answer.
 *
 * 48 on a phone is the hourly +48 h run exactly, so the common case is native
 * there too, and sub-hourly HRRR strides to half-hourly instead of to 1h45m.
 * A phone is where the bandwidth is worth spending a number on.
 */
export const FRAMES_MODEL_DESKTOP = 96
export const FRAMES_MODEL_PHONE = 48

let frameCount = FRAMES_DESKTOP
let modelFrameCount = FRAMES_MODEL_DESKTOP

export function setFrameBudget(n: number): void {
  frameCount = Math.max(1, n)
}

export function setModelFrameBudget(n: number): void {
  modelFrameCount = Math.max(1, n)
}

/** What a raster row states about its own values; the target is the row's own. */
type RasterReadout = { decimals: number; range?: [number, number] }

/** One nowCOAST WMS-T layer. Every entry below but the last two is only a row. */
function wms(
  id: string,
  label: string,
  group: LayerGroup,
  workspace: string,
  name: string,
  units: string,
  defaultOpacity: number,
  frameMode: FrameMode,
  readout: RasterReadout | null,
): LayerDef {
  const target: WmsTarget = {
    base: `${NOWCOAST}/${workspace}/wms`,
    layerName: name,
  }
  return {
    id,
    label,
    group,
    attribution: NOAA_ATTRIB,
    defaultOpacity,
    exclusive: true,
    timeline: 'live',
    units,
    legend: { kind: 'wms', ...target, units },
    readout: readout ? { kind: 'raster', ...readout, target } : null,
    create: () =>
      new WmsTimeLayer({
        ...target,
        id,
        attribution: NOAA_ATTRIB,
        opacity: defaultOpacity,
        frameCount,
        frameMode,
      }),
  }
}

/**
 * One SPC product. These are vectors rather than somebody's rendered imagery,
 * which is what lets the layer use the product's own colors, print its own
 * short code in the area and answer a tap with no network — see
 * docs/phase2.5-outlooks-plan.md.
 *
 * 0.45 by default: low enough that radar underneath stays readable, which is
 * the combination anyone watching a severe weather day actually wants, and
 * high enough to tell the categories apart.
 */
function spcOutlook(id: string, label: string, layer: number): LayerDef {
  return {
    id,
    label,
    group: 'outlooks',
    attribution: SPC_ATTRIB,
    defaultOpacity: 0.45,
    exclusive: true,
    timeline: null,
    units: '',
    // Esri serves no GeoServer colormap document, and a colour key for six
    // named categories asks the reader to match orange against orange. SPC's
    // own maps print ENH inside the enhanced area, and so does this one.
    legend: null,
    readout: { kind: 'feature' },
    create: () =>
      new GeoJsonLayer({
        id,
        // `dn>0` runs server-side and drops the Day 4-8 placeholder for 42
        // bytes; parseOutlooks drops it again, so the guard survives a
        // fallback to SPC's static files.
        url: queryUrl(OUTLOOKS, layer, 'dn>0'),
        parse: parseOutlooks,
        attribution: SPC_ATTRIB,
        opacity: 0.45,
        // Outlooks are issued on a fixed schedule; five minutes is generous.
        refreshSeconds: 300,
        labelled: true,
      }),
  }
}

// The fallback only: a model layer replaces this with the catalog's own
// attribution once `/api/models` lands, because which NOAA product is on
// screen is something only the catalog knows. Naming HRRR here would credit it
// for a GFS plate.
const NODD_ATTRIB =
  '<a href="https://registry.opendata.aws/noaa-hrrr-pds/">NOAA model data via NODD</a>'

/**
 * The models, in the order the picker shows their chips.
 *
 * A label and a list of field ids, and nothing else: what a field *is* —
 * units, decimals, the plotted range, the floor, the colours — comes from
 * `/api/models`, because the backend registry is what coloured the pixels.
 * Restating any of that here is the second list `GROUP_LABELS` already taught
 * us about.
 *
 * A row whose (model, field) the deployment does not ingest is absent from the
 * catalog, and `ModelPicker` hides it. That is the same quiet degradation as
 * "no model data at all", which this app already knows how to be in.
 */
const MODELS: {
  id: string
  label: string
  fields: string[]
  /**
   * The fields this model also draws as lines or arrows. One entry per
   * (field, kind), because a contoured field and its plate are two rows —
   * see the exclusivity note under `modelVector`.
   *
   * It is a second list *in the same table*, which is the same shape `fields`
   * already is and the same bargain: `initLayers()` runs synchronously before
   * the first render, so a row's id has to exist without waiting for the
   * catalog. Which of them are actually *offered* is still the catalog's
   * answer — a deployment that writes no contours has no `vectors` on the
   * variable, and `ModelPicker` hides the row.
   */
  vectors?: { field: string; kind: VectorKind }[]
}[] = [
  {
    id: 'hrrr',
    label: 'HRRR',
    fields: ['refc', 't2m', 'd2m', 'wind10', 'gust', 'apcp1h', 'cape', 'mslp', 'hgt500'],
    vectors: [
      { field: 'mslp', kind: 'contour' },
      { field: 'hgt500', kind: 'contour' },
      { field: 'wind10', kind: 'barb' },
    ],
  },
  // The 15-minute product is its own model row, which is the whole of what
  // sub-hourly costs now that the canonical step key is lead *minutes*.
  // `prate` is the nowcast's cross-check (Stage D) before it is a map layer.
  { id: 'hrrr-subh', label: 'HRRR 15m', fields: ['refc', 'prate'] },
  {
    id: 'rap',
    label: 'RAP',
    fields: ['refc', 't2m', 'd2m', 'wind10', 'gust', 'apcp1h', 'cape', 'mslp', 'hgt500'],
    vectors: [
      { field: 'mslp', kind: 'contour' },
      { field: 'hgt500', kind: 'contour' },
      { field: 'wind10', kind: 'barb' },
    ],
  },
  {
    id: 'nam',
    label: 'NAM',
    fields: ['refc', 't2m', 'd2m', 'wind10', 'gust', 'apcp1h', 'cape', 'mslp', 'hgt500'],
    vectors: [
      { field: 'mslp', kind: 'contour' },
      { field: 'hgt500', kind: 'contour' },
      { field: 'wind10', kind: 'barb' },
    ],
  },
  // NBM has no barbs and no 500 mb heights, and both are absence rather than
  // a special case: its `core` product carries no upper-air field, and its
  // `WIND:10 m above ground` is a *speed* with no components to take a
  // direction from.
  {
    id: 'nbm',
    label: 'NBM',
    fields: ['t2m', 'd2m', 'wind10', 'gust', 'apcp1h', 'pop1h'],
  },
  {
    id: 'gfs',
    label: 'GFS',
    fields: ['refc', 't2m', 'd2m', 'wind10', 'gust', 'apcp1h', 'cape', 'mslp', 'hgt500'],
    vectors: [
      { field: 'mslp', kind: 'contour' },
      { field: 'hgt500', kind: 'contour' },
      { field: 'wind10', kind: 'barb' },
    ],
  },
]

/**
 * A short name per field, for the stack list and the map menu.
 *
 * The catalog carries the long one (`Composite reflectivity`) and the picker
 * prefers it; this exists because `LayerDef.label` has to be a string
 * *synchronously*, before the catalog lands, and "HRRR hrrr-refc" in someone's
 * saved stack is not a label. One table, not 37 strings.
 */
const FIELD_LABELS: Record<string, string> = {
  refc: 'reflectivity',
  t2m: '2 m temperature',
  d2m: '2 m dewpoint',
  wind10: '10 m wind',
  gust: 'wind gust',
  apcp1h: '1-hour precipitation',
  cape: 'surface CAPE',
  mslp: 'sea level pressure',
  hgt500: '500 mb height',
  pop1h: 'precipitation chance',
  prate: 'precipitation rate',
}

/** What a vector row is called, appended to the field's own short name. The
 *  catalog's label is the *field's*; this says which artifact of it is drawn. */
const VECTOR_LABELS: Record<VectorKind, string> = {
  contour: 'contours',
  barb: 'barbs',
}

/**
 * One field from one of our own models.
 *
 * The id is a constant rather than something read out of the catalog because
 * `initLayers()` runs *synchronously before the first render* (prefs have to
 * be applied before the map mounts, and `isLayerId()` validates persisted ids
 * in that same tick). An async catalog would mean a persisted `hrrr-refc` is
 * rejected at boot and silently dropped from someone's saved stack. So the
 * rows are generated at module load from the constant tables above — still
 * static, still synchronous, and one entry per model rather than one per
 * (model, field).
 */
function modelField(model: string, modelLabel: string, variable: string): LayerDef {
  const id = `${model}-${variable}`
  return {
    id,
    label: `${modelLabel} ${FIELD_LABELS[variable] ?? variable}`,
    group: 'models',
    model,
    variable,
    ground: 'light',
    attribution: NODD_ATTRIB,
    defaultOpacity: 0.75,
    exclusive: true,
    timeline: 'run',
    // The API's, not ours. `probe.ts` and `Legend.svelte` read the catalog.
    units: '',
    legend: { kind: 'model', model, variable },
    readout: { kind: 'model', model, variable },
    create: () =>
      new ModelLayer({
        id,
        model,
        variable,
        attribution: NODD_ATTRIB,
        opacity: 0.75,
        frameCount: modelFrameCount,
        // Read at create time, which is why changing either rebuilds the
        // layer rather than mutating it. `MapView` pushes both in.
        window: modelWindow(),
        run: modelRun(model),
      }),
  }
}

/**
 * One model field drawn as isolines or wind barbs.
 *
 * **A vector row is separate from its plate, and both are `exclusive: true` in
 * the `models` group.** That is a real limitation and it is being accepted
 * rather than papered over: the obvious product is MSLP contours *over* a
 * temperature plate, and the exclusivity rule that keeps one timeline active
 * forbids it. The rule exists for a good measured reason — two clocks on one
 * slider is an animation that animates nothing — but two *model* rows sharing
 * a model, a run and a lead window are on the **same** clock, and the rule as
 * written does not know that. Widening it means making the exclusivity key the
 * *clock* rather than the group, and it touches the persisted stack,
 * `initLayers()`'s validation and `registry.test.ts`'s timeline invariant. It
 * is on the ROADMAP's Backburner list, which has the shape of the fix.
 *
 * The readout is the *field's*, not the line's: tapping an isobar layer should
 * report the pressure at that point, which is exactly what the plate's readout
 * already does and what `ModelVectorLayer.currentLead()` already resolves.
 */
function modelVector(
  model: string,
  modelLabel: string,
  variable: string,
  kind: VectorKind,
): LayerDef {
  const id = `${model}-${variable}-${kind}`
  return {
    id,
    label: `${modelLabel} ${FIELD_LABELS[variable] ?? variable} ${VECTOR_LABELS[kind]}`,
    group: 'models',
    model,
    variable,
    vector: kind,
    attribution: NODD_ATTRIB,
    // Ink rather than a wash: an isobar at 0.75 over a light basemap is grey
    // on grey, and there is nothing underneath it to keep readable.
    defaultOpacity: 0.9,
    exclusive: true,
    timeline: 'run',
    units: '',
    legend: { kind: 'vector', model, variable, vector: kind },
    readout: { kind: 'model', model, variable },
    create: () =>
      new ModelVectorLayer({
        id,
        model,
        variable,
        kind,
        attribution: NODD_ATTRIB,
        opacity: 0.9,
        frameCount: modelFrameCount,
        window: modelWindow(),
        run: modelRun(model),
      }),
  }
}

const MODEL_LAYERS: LayerDef[] = MODELS.flatMap((m) => [
  ...m.fields.map((field) => modelField(m.id, m.label, field)),
  ...(m.vectors ?? []).map((v) => modelVector(m.id, m.label, v.field, v.kind)),
])

/**
 * The short name for a model's chip, in registry order.
 *
 * The catalog's label is the long one — `NBM 2.5 km`, `GFS 0.25°` — which
 * belongs in the status panel and does not fit a 44 px chip in a row of five.
 * This is the same table the rows are generated from, not a second one.
 */
export const MODEL_CHIPS: readonly { id: string; label: string }[] = MODELS.map((m) => ({
  id: m.id,
  label: m.label,
}))

export const LAYERS: readonly LayerDef[] = [
  wms(
    'radar-reflectivity',
    'Radar (base reflectivity)',
    'radar',
    'weather_radar',
    'weather_radar:conus_base_reflectivity_mosaic',
    'dBZ',
    0.75,
    'recent',
    { decimals: 0, range: [0, 80] },
  ),
  {
    id: 'radar-iem',
    label: 'Radar (Iowa State cache)',
    group: 'radar',
    attribution: IEM_ATTRIB,
    defaultOpacity: 0.75,
    exclusive: true,
    timeline: 'live',
    units: 'dBZ',
    // A tile cache serves pictures, not values; there is nothing to query.
    readout: null,
    // IEM's tile cache publishes no colormap document; the ramp is NWS N0Q,
    // same as the primary, so the primary's bar would be a guess about
    // somebody else's rendering. No legend is more honest than a plausible one.
    legend: null,
    create: () =>
      new XyzFramesLayer({
        id: 'radar-iem',
        base: 'https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0',
        product: 'nexrad-n0q-900913',
        attribution: IEM_ATTRIB,
        opacity: 0.75,
      }),
  },
  {
    // The last hour of MRMS and the next one, drawn by us (nowcast Stage E).
    // Exclusive with nowCOAST's mosaic in the Radar group: two radars is
    // noise. On the `live` clock, because its past half *is* the radar loop;
    // the frames after now are what `observedUntil` marks.
    id: 'radar-nowcast',
    label: 'Radar + next hour',
    group: 'radar',
    attribution: MRMS_ATTRIB,
    defaultOpacity: 0.75,
    exclusive: true,
    timeline: 'live',
    units: '',
    legend: { kind: 'nowcast' },
    readout: { kind: 'nowcast' },
    create: () =>
      new RadarMapLayer({
        id: 'radar-nowcast',
        attribution: MRMS_CREDIT,
        opacity: 0.75,
        frameCount,
      }),
  },
  wms(
    'lightning-density',
    // Named for the thing rather than the measurement: the map menu prints
    // this label against "0.0 strikes/1000 km²/min", and a label as wide as
    // its own units is a label truncated to "Light…" on a phone.
    'Lightning',
    'observations',
    'lightning_detection',
    'lightning_detection:ldn_lightning_strike_density',
    // nowCOAST states the layer's units as strikes per square km per minute
    // "multiplied by a scaling factor of 10^3", and its colormap runs 0–300 on
    // that scale. Said per 1000 km² here instead: the identical number with the
    // scaling folded into the unit rather than printed as a power of ten.
    'strikes/1000 km²/min',
    0.8,
    'recent',
    // No range: probed 2026-09-17, this layer carries no in-band sentinel.
    // 2.1 over the Yucatán, 0 across a quiet CONUS, and outside its own bbox
    // the response has no features at all, which already reads as "—".
    { decimals: 1 },
  ),
  wms(
    'sat-visible',
    'Satellite (visible)',
    'satellite',
    'satellite',
    'satellite:goes_visible_imagery',
    'reflectance',
    0.6,
    'recent',
    null,
  ),
  wms(
    'sat-ir',
    'Satellite (infrared)',
    'satellite',
    'satellite',
    'satellite:goes_longwave_imagery',
    'brightness temp',
    0.6,
    'recent',
    null,
  ),
  wms(
    'sat-wv',
    'Satellite (water vapor)',
    'satellite',
    'satellite',
    'satellite:goes_water_vapor_imagery',
    'brightness temp',
    0.6,
    'recent',
    null,
  ),
  // One entry in `MODELS` per model, and that is the whole cost of a model.
  // What each field *is* comes from `/api/models`, and a row whose model or
  // field the deployment does not ingest hides itself once the catalog lands.
  ...MODEL_LAYERS,
  spcOutlook('spc-day1-cat', 'Day 1 categorical', 1),
  spcOutlook('spc-day1-torn', 'Day 1 tornado probability', 3),
  spcOutlook('spc-day1-hail', 'Day 1 hail probability', 5),
  spcOutlook('spc-day1-wind', 'Day 1 wind probability', 7),
  spcOutlook('spc-day2-cat', 'Day 2 categorical', 9),
  spcOutlook('spc-day3-cat', 'Day 3 categorical', 17),
  {
    id: 'nhc-storms',
    label: 'Tropical cyclones',
    group: 'tropical',
    attribution: NHC_ATTRIB,
    // The lines and points are ink and want to be read; the cone is drawn at
    // a third of this (`fillFactor`) so they sit on it rather than under it.
    defaultOpacity: 0.9,
    // Not exclusive: a cone over the seven-day outlook is NHC's own map, and
    // either over radar or a model plate is the reason to have both.
    exclusive: false,
    timeline: null,
    units: '',
    // The cone, the track, H/S/D/M and the four coastal colours are a
    // convention with no ramp to draw.
    legend: null,
    readout: { kind: 'feature' },
    create: () =>
      new GeoJsonLayer({
        id: 'nhc-storms',
        url: stormUrls(),
        parse: parseStorms,
        attribution: NHC_ATTRIB,
        opacity: 0.9,
        fillFactor: 0.35,
        // Advisories are three- to six-hourly; five minutes catches an
        // intermediate one within a cup of coffee and is four small requests.
        refreshSeconds: 300,
        labelled: true,
        points: true,
      }),
  },
  {
    id: 'nhc-outlook',
    label: '7-day tropical outlook',
    group: 'tropical',
    attribution: NHC_ATTRIB,
    defaultOpacity: 0.45,
    exclusive: false,
    timeline: null,
    units: '',
    // Three named risks, printed as the percentage inside each area.
    legend: null,
    readout: { kind: 'feature' },
    create: () =>
      new GeoJsonLayer({
        id: 'nhc-outlook',
        url: outlookUrl(),
        parse: parseOutlook,
        attribution: NHC_ATTRIB,
        opacity: 0.45,
        // Issued every six hours.
        refreshSeconds: 600,
        labelled: true,
      }),
  },
  {
    id: 'alerts',
    label: 'Watches & warnings',
    group: 'hazards',
    attribution: NOAA_ATTRIB,
    defaultOpacity: 0.55,
    // Not exclusive: hazards overlay everything else by design, and for the
    // same reason they are on no clock — the tiles carry no TIME at all.
    exclusive: false,
    timeline: null,
    units: '',
    // Categorical, ~200 polygon rules rather than a colormap; the map menu
    // names each alert instead.
    legend: null,
    // Not a number: the menu lists the alerts themselves.
    readout: null,
    create: () =>
      new WmsStaticLayer({
        id: 'alerts',
        base: `${NOWCOAST}/alerts/wms`,
        layerName: ALERTS_LAYER_NAME,
        attribution: NOAA_ATTRIB,
        opacity: 0.55,
        // Alerts change constantly and the tiles carry no TIME to distinguish
        // them, so this is the one layer that goes stale in place.
        refreshSeconds: 60,
      }),
  },
  {
    id: MD_ID,
    label: 'Mesoscale discussions',
    group: 'hazards',
    attribution: SPC_ATTRIB,
    // Outline only, so it has to read clearly over whatever is beneath it.
    defaultOpacity: 0.9,
    // An MD over a warning polygon is exactly what someone watching a severe
    // weather day wants to see.
    exclusive: false,
    timeline: null,
    units: '',
    legend: null,
    // Not a number: the menu lists the discussions and opens their text.
    readout: null,
    create: () =>
      new GeoJsonLayer({
        id: MD_ID,
        url: queryUrl(MD_SERVICE, 0),
        parse: parseMds,
        attribution: SPC_ATTRIB,
        opacity: 0.9,
        // The service carries only active discussions and they come and go
        // within the hour, so this matches the alerts layer's cadence.
        refreshSeconds: 60,
        filled: false,
        labelled: true,
        lineWidth: 2,
      }),
  },
]

/** What a first visit shows: radar, because that is what a weather map is for. */
export const DEFAULT_ACTIVE: readonly string[] = ['radar-reflectivity']

export const GROUP_LABELS: Record<LayerGroup, string> = {
  radar: 'Radar',
  observations: 'Observations',
  satellite: 'Satellite',
  models: 'Models',
  outlooks: 'SPC outlooks',
  tropical: 'Tropical',
  hazards: 'Hazards',
}

export function getLayerDef(id: string): LayerDef | undefined {
  return LAYERS.find((l) => l.id === id)
}

/**
 * The ground the active stack needs under a Match theme basemap: light when any
 * active row asks for it, otherwise no opinion and the theme decides.
 */
export function groundFor(ids: readonly string[]): BasemapTone | null {
  return ids.some((id) => getLayerDef(id)?.ground === 'light') ? 'light' : null
}

export function isLayerId(id: unknown): id is string {
  return typeof id === 'string' && LAYERS.some((l) => l.id === id)
}

/**
 * Layers offered in the control. The IEM radar is a fallback for when nowCOAST
 * is down, not a second thing to choose between, so it stays out of the list
 * until something asks for it.
 */
export function selectableLayers(includeFallbacks = false): LayerDef[] {
  return LAYERS.filter((l) => includeFallbacks || l.id !== 'radar-iem')
}
